/**
 * Environnement de reconnaissance vocale du navigateur — sert uniquement à
 * adapter les MESSAGES d'erreur (jamais à bloquer une fonctionnalité) :
 * `service-not-allowed` n'a pas le même remède sur un Mac, dans Safari iOS ou
 * dans Chrome iOS.
 *
 * Constat terrain : Safari iOS fonctionne, Chrome/Firefox/Edge iOS non. Ces
 * navigateurs exposent `webkitSpeechRecognition` (WKWebView) mais Apple ne
 * l'active que pour Safari lui-même — l'appel échoue en `service-not-allowed`
 * même avec la Dictée activée dans les Réglages, et aucun code web ne peut
 * demander cette permission à leur place.
 */

export interface SpeechEnv {
  /** iPhone/iPad (y compris iPadOS, qui se présente comme un Mac). */
  isIos: boolean;
  /** iOS mais PAS Safari : Chrome/Firefox/Edge iOS, navigateurs intégrés (WKWebView tiers). */
  isIosNonSafari: boolean;
}

interface NavigatorLike {
  userAgent: string;
  platform?: string;
  maxTouchPoints?: number;
}

const IOS_THIRD_PARTY_BROWSERS = /(CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|DuckDuckGo|GSA\/)/;

export function detectSpeechEnv(
  nav: NavigatorLike | undefined = typeof navigator === 'undefined' ? undefined : navigator,
): SpeechEnv {
  if (!nav) return { isIos: false, isIosNonSafari: false };
  const ua = nav.userAgent;
  // iPadOS 13+ envoie un UA de Mac : seul le tactile le distingue d'un vrai Mac.
  const isIpadOs = nav.platform === 'MacIntel' && (nav.maxTouchPoints ?? 0) > 1;
  const isIos = /iPhone|iPad|iPod/.test(ua) || isIpadOs;
  // Safari iOS porte le jeton « Safari/ » ; une WKWebView tierce (navigateur
  // intégré d'une app) ne l'a pas.
  const isIosNonSafari = isIos && (IOS_THIRD_PARTY_BROWSERS.test(ua) || !/Safari\//.test(ua));
  return { isIos, isIosNonSafari };
}
